import { z } from 'zod';
import { SKILL_STREAM_SCHEMAS, type SkillStreamEventName } from '@shared/schemas/skill-stream-events';
import { logger } from './logger';
import { validateResponse } from './validateResponse';
import { safeParseJson } from '@shared/parse-json';

/**
 * Trust-boundary parser for the skill SSE stream (server -> browser).
 *
 * Every consumer of the stream (`useSkillStream`, `useSkillRun`, ...) gets its
 * events off the wire as strings; `parseSkillStreamEvent` is the single place
 * a wire string becomes a typed payload, so validation happens there, not
 * downstream. The schemas and payload types live in
 * `src/shared/schemas/skill-stream-events.ts`, shared with the producer
 * (`src/services/skills/sdk/sse-writer.ts`).
 */

/** Type guard: `value` is a plain object (not null, not an array). */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Parse a skill SSE event payload off the wire and validate against the
 * producer's schema. Returns null on invalid JSON or when the event
 * name is unknown. Logs drift on schema mismatch via validateResponse
 * but returns the best-effort parsed data.
 *
 * This is the trust boundary for the skill SSE stream — the only
 * place we cross from wire string to typed payload.
 */
export function parseSkillStreamEvent<E extends SkillStreamEventName>(
  eventName: E,
  data: string,
): z.infer<(typeof SKILL_STREAM_SCHEMAS)[E]> | null {
  const decoded = safeParseJson(z.unknown(), data);
  if (!decoded.success) {
    logger.warn({ err: decoded.error, eventName }, '[skill-stream] JSON parse failed');
    return null;
  }
  const raw = decoded.data;
  // Indexed-access over a heterogeneous schema map: TS can't prove
  // `SCHEMAS[E]` is `ZodType<z.infer<SCHEMAS[E]>>` when E is generic,
  // because the individual schemas aren't mutually assignable. The
  // runtime lookup does match eventName, so this is safe in practice.
  const schema = SKILL_STREAM_SCHEMAS[eventName] as unknown as z.ZodType<
    z.infer<(typeof SKILL_STREAM_SCHEMAS)[E]>
  >;
  return validateResponse(schema, raw, `sse:${eventName}`);
}
